import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf'; // Import jsPDF
import { MapPin, Phone, Mail, Clock } from 'lucide-react'; // Import Lucide icons
import './ContactSection.css'; // Ensure your CSS is in this file

// ReceiptPopup component - now accepts onDownloadPdf prop
const ReceiptPopup = ({ data, onClose, onDownloadPdf }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    try {
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString; // Fallback to original string if invalid date
    }
  };

  return (
    <>
      <style>
        {`
          .popup-backdrop {
            position: fixed;
            inset: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            animation: fadeIn 0.3s ease-out; /* Added fade in for backdrop */
          }

          .popup-container {
            background-color: #ffffff;
            padding: 24px;
            border-radius: 12px;
            max-width: 400px;
            width: 90%; /* Make it slightly more responsive */
            position: relative;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
            animation: fadeInUp 0.3s ease-out;
          }

          .popup-close {
            position: absolute;
            top: 8px;
            right: 12px;
            font-size: 20px;
            font-weight: bold;
            color: #555;
            background: none;
            border: none;
            cursor: pointer;
          }

          .popup-close:hover {
            color: #000;
          }

          .popup-title {
            font-size: 24px;
            font-weight: 600;
            text-align: center;
            color: #1E1E1E;
            margin-bottom: 16px;
          }

          .popup-info p {
            font-size: 14px;
            color: #444;
            margin: 6px 0;
          }

          .popup-buttons {
            margin-top: 20px;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .popup-buttons button {
            padding: 10px 16px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            border: none;
            transition: background-color 0.3s ease;
          }

          .popup-buttons .download {
            background-color: #1E1E1E;
            color: white;
          }

          .popup-buttons .download:hover {
            background-color: black;
          }

          .popup-buttons .close {
            background-color: #f2f2f2;
            color: #333;
          }

          .popup-buttons .close:hover {
            background-color: #e0e0e0;
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(40px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      <div className="popup-backdrop">
        <div className="popup-container">
          <button onClick={onClose} className="popup-close">×</button>
          <h2 className="popup-title">Reservation Confirmed 🎉</h2>
          <div className="popup-info">
            <p><strong>Name:</strong> {data.name}</p>
            <p><strong>Email:</strong> {data.email}</p>
            <p><strong>Phone:</strong> {data.phone}</p>
            <p><strong>Date:</strong> {formatDate(data.date)}</p>
            <p><strong>Time:</strong> {data.time}</p>
            <p><strong>Persons:</strong> {data.persons}</p>
            <p><strong>Status:</strong> {data.status}</p>
          </div>
          <div className="popup-buttons">
            {/* Call onDownloadPdf passing the data and formatDate function */}
            <button className="download" onClick={() => onDownloadPdf(data, formatDate)}>Download Receipt</button>
            <button className="close" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </>
  );
};

// ContactSection component
const ContactSection = ({ id }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const sectionRef = useRef(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const address = 'The Porch Cafe & Restaurant, Rooftop Area of Hotel The Pride, 267, Rajeev Gandhi Nagar, Near Allen Sankalp, Kota, Rajasthan (324005)';
  const phoneNumber1 = '919024283004';
  const phoneNumber2 = '919602679997';
  const emailAddress = 'info@theporchcafe.in';


  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    persons: '',
    status: 'pending',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getScrollAnimatedStyle = (delay) => ({
    transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
    opacity: isVisible ? 1 : 0,
    transition: `transform 0.8s ease-out ${delay}s, opacity 0.8s ease-out ${delay}s`,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    // For 'persons', ensure it's stored as a number, or empty string if cleared
    if (name === "persons") {
      setFormData((prev) => ({ ...prev, [name]: value === '' ? '' : Number(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

    // PDF Download Function - now defined within ContactUs
  const handleDownloadPdf = (dataToPdf, formatDateFunc) => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let y = 20;

    // Cafe name and address
    doc.setFontSize(18);
    doc.setTextColor(212, 167, 98); // Cafe gold color
    doc.setFont("helvetica", "bold");
    doc.text("The Porch Cafe & Restaurant", pageWidth / 2, y, { align: "center" });

    y += 8;
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "normal");
    doc.text("Rooftop Area of Hotel The Pride, 267, Rajeev Gandhi Nagar,", pageWidth / 2, y, { align: "center" });
    y += 5;
    doc.text("Near Allen Sankalp, Kota, Rajasthan (324005)", pageWidth / 2, y, { align: "center" });

    y += 10;
    doc.setDrawColor(212, 167, 98); // golden divider
    doc.line(margin, y, pageWidth - margin, y);

    y += 10;
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("Cafe Booking Details", pageWidth / 2, y, { align: "center" });

    y += 12;

    const detailSection = (title, items) => {
      doc.setTextColor(212, 167, 98);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(title, margin, y);
      y += 7;

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(33, 33, 33);

      items.forEach(({ label, value }) => {
        doc.text(`${label}:`, margin, y);
        doc.text(`${value || '-'}`, margin + 50, y);
        y += 6;
      });

      y += 5;
    };

    detailSection("Guest Information", [
      { label: "Guest Name", value: dataToPdf.name },
      { label: "Email", value: dataToPdf.email },
      { label: "Phone", value: dataToPdf.phone },
      { label: "Number of Persons", value: dataToPdf.persons },
    ]);

    detailSection("Booking Information", [
      { label: "Booking Date", value: formatDateFunc(dataToPdf.date) },
      { label: "Booking Time", value: dataToPdf.time },
      { label: "Booking Status", value: dataToPdf.status }, // Ensure status is passed in receiptData
    ]);

    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text("Thank you for choosing The Porch Cafe & Restaurant!", pageWidth / 2, y + 10, { align: "center" });

    doc.save(`Reservation_${dataToPdf.name || 'Guest'}_${Date.now()}.pdf`);
  };

const [alert, setAlert] = useState({ show: false, message: '', type: '' });

// Helper to auto-hide the alert
const showAlert = (message, type = 'error') => {
  setAlert({ show: true, message, type });
  setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
};
const handleSubmit = async (e) => {
  e.preventDefault();

  const { name, email, phone, date, time, persons, status } = formData;
  
  // --- Client-side validation ---
  if (!name || !email || !phone || !date || !time || persons === '' || !status) {
    showAlert('Please fill in all required fields.');
    return;
  }

  const parsedPersons = parseInt(persons, 10);
  if (isNaN(parsedPersons) || parsedPersons < 1) {
    showAlert('Number of persons must be a positive number.');
    return;
  }

  try {
    const payload = { ...formData, persons: parsedPersons };

    const res = await axios.post('https://admin.theporch.in/api/cafe_reservations', payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    if (res.status === 201) {
      setReceiptData(payload);
      setShowReceipt(true);
      showAlert('Reservation successful!', 'success'); // Success feedback
      setFormData({
        name: '', email: '', phone: '', date: '',
        time: '', persons: '', status: 'pending',
      });
    } else {
      showAlert('Unexpected response from server.');
    }
  } catch (error) {
    console.error('❌ Error submitting reservation:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Server error';
    showAlert(`Submission failed: ${errorMessage}`);
  }
};

  // Google Maps URLs
  // The directions URL can still use encodeURIComponent(address) if you wish to provide directions
  const googleMapsDirectionsUrl = `http://maps.google.com/maps?q=${encodeURIComponent(address)}`;
  // The embed URL is now directly from your provided string
  const googleMapsEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3235.7011309951695!2d75.84578947489031!3d25.137535077749465!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396f85c82fa55981%3A0xe56d0b917178f5a4!2sThe%20Porch%20Cafe%20%26%20Restaurant!5e1!3m2!1sen!2sin!4v1753060005620!5m2!1sen!2sin";

  return (
    <>
      <section id={id} className="contact-container-full-height" ref={sectionRef}>
        {/* Left Section - Reservation Form */}
        <div className="contact-left">
          <div className="sub-heading" style={getScrollAnimatedStyle(0.1)}>Reserve a Table</div>
          <div className="main-heading" style={getScrollAnimatedStyle(0.3)}>
            Reserve Now Your <br /> Table & Enjoy <br />
            <span className="highlighted">Dining Experience</span>
          </div>
          <div className="open-hours" style={getScrollAnimatedStyle(0.5)}>
            <strong>Open Hours</strong>
            <div className="hours-row">
              <span>Mon - Sun</span>
              <span>11:00 AM TO 11:00 PM</span>
            </div>
          </div>
        </div>

        {/* Right Section - Form */}
        <div className="contact-right" style={getScrollAnimatedStyle(0.7)}>
          <div className="form-group">
            <label htmlFor="name">Your Name</label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Enter your name" required />
          </div>

          <div className="form-row">
            <div className="form-group form-half">
              <label htmlFor="email">Email Address</label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter email" required />
            </div>
            <div className="form-group form-half">
              <label htmlFor="phone">Phone Number</label>
              <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="Enter phone" required />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-third">
              <label htmlFor="date">Date</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]} // Disable back dates
                required
              />
            </div>
            <div className="form-group form-third">
              <label htmlFor="time">Time</label>
              <input type="time" id="time" name="time" value={formData.time} onChange={handleChange} required />
            </div>
            <div className="form-group form-third">
              <label htmlFor="persons">No. Of Person</label>
              <input type="number" id="persons" min="1" name="persons" value={formData.persons} onChange={handleChange} placeholder="-" required />
            </div>
          </div>

          <button className="submit-button" onClick={handleSubmit} style={getScrollAnimatedStyle(0.9)}>
            Reserve Now
          </button>
        </div>
      </section>
{alert.show && (
  <div style={{
    position: 'fixed',
    top: '20px',
    right: '20px',
    padding: '15px 25px',
    borderRadius: '8px',
    backgroundColor: alert.type === 'success' ? '#d4edda' : '#f8d7da',
    color: alert.type === 'success' ? '#155724' : '#721c24',
    border: `1px solid ${alert.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    zIndex: 1000,
    transition: 'all 0.3s ease'
  }}>
    <strong>{alert.type === 'success' ? 'Success: ' : 'Error: '}</strong>
    {alert.message}
    <button 
      onClick={() => setAlert({ ...alert, show: false })}
      style={{ marginLeft: '15px', cursor: 'pointer', border: 'none', background: 'none', fontWeight: 'bold' }}
    >
      ×
    </button>
  </div>
)}
    

      {showReceipt && <ReceiptPopup data={receiptData} onClose={() => setShowReceipt(false)} onDownloadPdf={handleDownloadPdf} />}
    </>
  );
};

export default ContactSection;